import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useReliability } from '@/hooks/useConsoleData';
import {
  STATUS_LABEL, TARGETS_URL, barWidth, burnLabel, failureHref, fetchReliability, kindLabel, observedLabel, pct, sampleLabel, signInLine, targetLabel,
  type Indicator, type Reliability, type ReliabilityStatus, type SignIns, type Surface,
} from '@/lib/reliability-data';
import { when } from '@/lib/format';
import s from './console.module.css';

/**
 * Reliability: the error budget per surface, live. The weekly script
 * (command-center tools/error-budget) read from the database on request:
 * for each surface its indicators, the share that went well, the burn
 * against the budget (1.0 spends the window's whole budget) and the rows
 * behind an over-budget indicator, each opening its session autopsy.
 *
 * The targets are proposed and say so. A value nobody measured is the words
 * with the reason, never 0. Ids and codes only: no names, nothing typed.
 */

const chipClass: Record<ReliabilityStatus, string> = {
  within: s.relChipWithin,
  over: s.relChipOver,
  'not enough data': s.relChipNone,
  'not measured': s.relChipNone,
};

export function StatusChip({ status }: { status: ReliabilityStatus }) {
  return <span className={`${s.pill} ${chipClass[status]}`} data-status={status}>{STATUS_LABEL[status]}</span>;
}

export function BurnBar({ burn }: { burn: number | null }) {
  const w = barWidth(burn);
  return (
    <span className={s.relBurn} data-burn={burn === null ? 'none' : String(burn)}>
      <span className={s.relTrack} aria-hidden="true">
        {w !== null && <span className={`${s.relFill} ${burn !== null && burn > 1 ? s.relFillOver : ''}`} style={{ width: `${w}%` }} />}
        <span className={s.relOne} title="1.0: the window's whole budget" />
      </span>
      <span className={s.relBurnNum}>{burnLabel(burn)}</span>
    </span>
  );
}

function FailureLinks({ i, windowLabel }: { i: Indicator; windowLabel: string }) {
  if (i.failures.length === 0) return <span className={s.muted}>No rows were recorded behind it.</span>;
  return (
    <span className={s.relFailures} data-failures={i.key}>
      Behind it ({i.failures.length === i.failuresTotal ? i.failuresTotal : `newest ${i.failures.length} of ${i.failuresTotal}`}):{' '}
      {i.failures.map((f, idx) => (
        <span key={`${f.kind}:${f.id}:${idx}`}>
          {idx > 0 && ' · '}
          <Link to={failureHref(f, windowLabel)} data-failure-id={f.id}>{kindLabel(f.kind)} {f.code}, {when(f.at, 'time not recorded')}</Link>
        </span>
      ))}
    </span>
  );
}

function IndicatorRow({ i, windowLabel }: { i: Indicator; windowLabel: string }) {
  return (
    <>
      <tr data-indicator={i.key} data-row-status={i.status}>
        <td data-label="Indicator"><strong>{i.label}</strong></td>
        <td data-label="Observed" data-cell="observed">
          {observedLabel(i)}
          {i.reason && <div className={s.sub} data-reason>{i.reason}</div>}
        </td>
        <td data-label="Sample" data-cell="sample">{sampleLabel(i)}{i.bad !== null && i.n !== null && i.max === null && <div className={s.sub}>{i.bad.toLocaleString()} bad</div>}</td>
        <td data-label="Target" data-cell="target">{targetLabel(i)}</td>
        <td data-label="Burn" data-cell="burn"><BurnBar burn={i.burn} /></td>
        <td data-label="Status"><StatusChip status={i.status} /></td>
      </tr>
      {i.status === 'over' && (
        <tr className={s.relBehind}><td colSpan={6} data-label="Behind it"><FailureLinks i={i} windowLabel={windowLabel} /></td></tr>
      )}
    </>
  );
}

function SurfaceTable({ sf, windowLabel }: { sf: Surface; windowLabel: string }) {
  const worst = sf.indicators.find((i) => i.key === sf.worstIndicator);
  return (
    <section className={`${s.section} ${s.relSurface}`} data-surface={sf.surface} data-surface-status={sf.status} aria-labelledby={`rel-${sf.surface}`}>
      <div className={s.relHead}>
        <h2 className={s.sectionTitle} id={`rel-${sf.surface}`}>{sf.label}</h2>
        <StatusChip status={sf.status} />
        <span className={s.sectionSummary} data-worst>
          {sf.worstBurn === null ? 'no indicator has enough data for a burn yet' : `worst burn ${burnLabel(sf.worstBurn)}${worst ? `, ${worst.label.toLowerCase()}` : ''}`}
        </span>
      </div>
      {sf.note && <p className={s.sub} style={{ margin: '0 16px 8px' }}>{sf.note}</p>}
      {sf.indicators.length === 0 ? <div className={s.empty} style={{ padding: '0 16px 12px' }}>No indicators for this surface.</div> : (
        <div className={s.relTableWrap}>
          <table className={`${s.table} ${s.relTable}`}>
            <thead><tr><th>Indicator</th><th>Observed</th><th>Sample</th><th>Target</th><th>Burn (1.0 is the budget)</th><th>Status</th></tr></thead>
            <tbody>{sf.indicators.map((i, idx) => <IndicatorRow key={`${i.key}:${idx}`} i={i} windowLabel={windowLabel} />)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Sign-ins kept, per app version: the phones' "signed in, then every call 401" shows up here first. */
export function SignInsTable({ si }: { si: SignIns }) {
  const secs = si.followUpSeconds ?? 60;
  return (
    <section className={s.section} data-panel="sign-ins" aria-labelledby="rel-sign-ins">
      <div className={s.relHead}>
        <h2 className={s.sectionTitle} id="rel-sign-ins">Sign-ins kept, per app version</h2>
        <span className={s.sectionSummary}>a sign-in we answered 200 with a token, then a 401 for the same client within {secs} seconds: the client did not keep its token</span>
      </div>
      {!si.measured ? (
        <div className={s.sub} style={{ padding: '0 16px 12px' }} data-reason>Not measured: {si.reason ?? 'the server did not say why'}.</div>
      ) : si.byVersion.length === 0 ? (
        <div className={s.empty} style={{ padding: '0 16px 12px' }}>No member sign-ins in the window.</div>
      ) : (
        <div className={s.relTableWrap}>
          <table className={`${s.table} ${s.relTable}`}>
            <thead><tr><th>App version</th><th>Surface</th><th>Sign-ins</th><th>Followed by a 401</th><th>Kept</th></tr></thead>
            <tbody>
              {si.byVersion.map((v, idx) => (
                <tr key={`${v.surface}:${v.appVersion}:${idx}`} data-sign-in-version={v.appVersion} data-broken={v.followedBy401 > 0 ? 'yes' : 'no'}>
                  <td data-label="App version"><strong>{v.appVersion}</strong></td>
                  <td data-label="Surface">{v.surface}</td>
                  <td data-label="Sign-ins">{v.signIns.toLocaleString()}</td>
                  <td data-label="Followed by a 401" data-cell="broken">{signInLine(v)}</td>
                  <td data-label="Kept" data-cell="kept">{v.keptShare === null ? `not enough data (${v.signIns} of the 5 needed)` : pct(v.keptShare)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function ReliabilityView({ r, onRefresh, refreshing }: { r: Reliability; onRefresh?: () => void; refreshing?: boolean }) {
  const over = r.surfaces.filter((x) => x.status === 'over');
  const u = r.unattributed;
  const unattributed = u.traces + u.dailyChecks + u.firstChecks + u.forms + u.signIns;
  const days = r.window.days;
  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>Reliability</h1>
        <span className={s.pageSub} data-panel="as-of">
          {days !== null ? `last ${days === 1 ? 'day' : `${Number.isInteger(days) ? days : days.toFixed(1)} days`}` : 'window not recorded'}
          {' · '}read {when(r.fetchedAt, 'time not recorded')}
          {r.cacheSeconds !== null && ` · held ${Math.round(r.cacheSeconds / 60)} min`}
          {' · '}team and QA left out
          {onRefresh && <> · <button type="button" className={s.linkBtn} onClick={onRefresh} disabled={refreshing}>{refreshing ? 'Reading the database…' : 'Read the database again'}</button></>}
        </span>
      </div>

      <div className={s.relRule} data-panel="freeze-rule">
        <strong>{r.freezeRule}</strong>{' '}
        <span className={s.muted}>Burn is the share that went wrong divided by the budget (1 minus the target); 1.0 spends the window's whole budget.</span>
      </div>

      <div className={s.notice} data-panel="targets">
        Proposed targets, Brian to confirm. They live in <a className={s.linkA} href={TARGETS_URL} target="_blank" rel="noreferrer"><code>{r.targets.source}</code></a> ({r.targets.status}).
      </div>

      <div className={s.strip} data-panel="surfaces">
        {r.surfaces.map((sf, idx) => (
          <a key={`${sf.surface}:${idx}`} href={`#rel-${sf.surface}`} className={`${s.stat} ${s.relStat}`} data-summary={sf.surface}>
            <span className={s.statLabel}>{sf.label}</span>
            <span className={s.statVal}>{sf.worstBurn === null ? 'no burn' : burnLabel(sf.worstBurn)}</span>
            <span><StatusChip status={sf.status} /></span>
          </a>
        ))}
      </div>

      {r.surfaces.length === 0 && <div className={s.empty} data-panel="empty">The server returned no surfaces. Nothing is measured here yet.</div>}
      {over.length > 0 && (
        <div className={s.error} data-panel="over">
          In freeze: {over.map((x) => x.label).join(', ')}. Only fixes, security and the change that restores the budget merge there until the weekly review shows it under 1.0.
        </div>
      )}
      {!r.sentry.measured && (
        <div className={s.sub} data-panel="sentry">Crash-free sessions and accepted error events are not measured: {r.sentry.reason ?? 'Sentry was not read'}.</div>
      )}

      {r.surfaces.map((sf, idx) => <SurfaceTable key={`${sf.surface}:${idx}`} sf={sf} windowLabel={r.window.label} />)}

      <SignInsTable si={r.signIns} />

      {unattributed > 0 && (
        <div className={s.sub} data-panel="unattributed">
          Not tied to any surface, so in no table above: {[
            u.traces ? `${u.traces} engine session${u.traces === 1 ? '' : 's'}` : null,
            u.dailyChecks ? `${u.dailyChecks} daily check${u.dailyChecks === 1 ? '' : 's'}` : null,
            u.firstChecks ? `${u.firstChecks} first check${u.firstChecks === 1 ? '' : 's'}` : null,
            u.forms ? `${u.forms} form${u.forms === 1 ? '' : 's'}` : null,
            u.signIns ? `${u.signIns} sign-in${u.signIns === 1 ? '' : 's'}` : null,
          ].filter(Boolean).join(', ')}. The row carried no platform and the member had no trace to borrow one from.
        </div>
      )}
      <p className={s.sub}>
        The same arithmetic as the weekly script (command-center <code>tools/error-budget/budget.py</code>). Under its floor an indicator has no burn and says how many it has and needs. An engine session the member left counts as clean unless the engine aborted or failed inside it; an aborted ending is the engine's, never the member's choice. <Link to={`/sessions/failed?since=${encodeURIComponent(r.window.label)}`}>Every session that did not finish</Link>
      </p>
    </div>
  );
}

export default function ReliabilityPage() {
  const q = useReliability();
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);
    try {
      qc.setQueryData(['reliability'], await fetchReliability(true));
    } finally {
      setRefreshing(false);
    }
  };
  if (q.isLoading) return <div className={s.empty}>Reading the traces, checks and forms…</div>;
  if (q.isError || !q.data) return <div className={s.notice}>The reliability numbers did not load. {q.error instanceof Error ? q.error.message : ''}</div>;
  return <ReliabilityView r={q.data} onRefresh={() => void refresh()} refreshing={refreshing} />;
}

/** The words of the Today line: which surfaces are over budget, or why none can be said to be. */
export const todayReliabilitySummary = (r: Reliability): string => {
  const over = r.surfaces.filter((x) => x.status === 'over');
  if (over.length) return `${over.length} ${over.length === 1 ? 'surface' : 'surfaces'} over budget: ${over.map((x) => `${x.label} ${burnLabel(x.worstBurn)}`).join(', ')}. Only fixes merge there.`;
  const measured = r.surfaces.filter((x) => x.status === 'within');
  if (measured.length) return `every surface with enough data is within budget (${measured.map((x) => x.label).join(', ')})`;
  return 'no surface has enough data for a verdict yet';
};

/** Today's Reliability line: the freeze count, opening the page. */
export function ReliabilityTodaySection() {
  const q = useReliability();
  const r = q.data;
  const over = r ? r.surfaces.filter((x) => x.status === 'over').length : null;
  return (
    <section className={s.section} data-panel="today-reliability" style={{ padding: '12px 16px', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      <span className={s.sectionTitle}>Reliability</span>
      <span className={s.sectionSummary}>
        {q.isError ? 'the error budget did not load' : r ? todayReliabilitySummary(r) : 'reading the error budget'}
      </span>
      {over !== null && <span className={s.sectionCount}>{over}</span>}
      <Link to="/reliability" className={s.linkA} style={{ marginLeft: over !== null ? 0 : 'auto', fontSize: '0.8rem' }}>Open the error budget</Link>
    </section>
  );
}
